/**
 * A MESMA conferencia do servidor, e com a mesma fronteira: encostar pode.
 *
 * `StatusBandService.refuseOverlap` no Spring e `StatusBands.refuseOverlap` no Kotlin
 * recusam a faixa gravada que cruza outra do mesmo dono no mesmo indicador -
 * `min < outra.max && outra.min < max` - e respondem com o trecho em comum.
 */
export function findOverlap(band, others) {
    for (const other of others) {
        if (other.level === band.level) {
            continue;
        }
        if (band.minValue < other.maxValue && other.minValue < band.maxValue) {
            return {
                level: band.level,
                other: other.level,
                from: Math.max(band.minValue, other.minValue),
                to: Math.min(band.maxValue, other.maxValue)
            };
        }
    }
    return undefined;
}
/**
 * A sobreposicao que SOBRA depois de todas as gravacoes - essa nenhuma ordem resolve.
 *
 * So conta par em que ao menos uma das faixas muda: uma sobreposicao antiga entre duas
 * faixas que ninguem tocou nao e o que se esta salvando, e o servidor nem a veria.
 */
export function finalOverlap(current, changes) {
    const final = applied(current, changes);
    for (const change of changes) {
        const overlap = findOverlap(change, final);
        if (overlap) {
            return overlap;
        }
    }
    return undefined;
}
/**
 * Em que ORDEM gravar as faixas mudadas de um indicador, para nenhuma gravacao
 * intermediaria ser recusada.
 *
 * ## Por que a ordem importa
 *
 * O servidor aceita uma faixa por requisicao, e confere cada uma contra as que JA
 * estao gravadas. Mover a fronteira entre "Otimo" (90-120) e "Bom" (120-130) para 125
 * sao duas gravacoes: alargar "Otimo" primeiro poe 90-125 contra o 120-130 que ainda
 * esta la, e e recusado; encolher "Bom" primeiro (125-130) abre um vao de 120 a 125,
 * que e permitido, e entao "Otimo" ocupa o vao. No sentido contrario (fronteira para
 * 115) a ordem se inverte - e por isso ela e decidida aqui, e nao fixa.
 *
 * ## Como
 *
 * Simula o estado do servidor a partir do que a tela carregou e grava primeiro quem
 * cabe: quem encolhe cabe sempre, quem cresce cabe depois que o vizinho saiu do
 * caminho. Quem ainda herda as faixas cai no mesmo caso: a primeira gravacao
 * materializa as quatro com o valor herdado, que e o que a tela mostra - o estado de
 * partida e o mesmo.
 *
 * Quando ninguem cabe - uma TROCA, em que cada faixa quer o lugar da outra -, uma
 * delas vai antes para um lugar livre acima de tudo, e volta depois. Custa uma
 * requisicao a mais, e so acontece nesse caso.
 *
 * Supoe que o estado FINAL nao tem sobreposicao (ver `finalOverlap`): com ela nenhuma
 * ordem resolve, e quem chama recusa antes de mandar.
 */
export function orderSaves(current, changes) {
    const state = new Map(current.map((band) => [band.level, band]));
    const pending = [...changes];
    const parked = new Set();
    const steps = [];
    // O lugar livre: acima de todo limite, o de agora e o pedido.
    let free = Math.max(0, ...current.map((band) => band.maxValue), ...changes.map((band) => band.maxValue));
    // Cada passo grava ou estaciona uma faixa, e cada uma estaciona no maximo uma vez.
    const limit = changes.length * 2 + 1;
    while (pending.length > 0) {
        if (steps.length > limit) {
            throw new Error('Não foi possível ordenar as faixas para salvar.');
        }
        const others = (level) => [...state.values()].filter((band) => band.level !== level);
        const index = pending.findIndex((change) => !findOverlap(change, others(change.level)));
        if (index >= 0) {
            const [change] = pending.splice(index, 1);
            state.set(change.level, change);
            steps.push(change);
            continue;
        }
        const stuck = pending.find((change) => !parked.has(change.level));
        if (!stuck) {
            throw new Error('Não foi possível ordenar as faixas para salvar.');
        }
        const park = { level: stuck.level, minValue: free, maxValue: free + 1 };
        free += 1;
        parked.add(stuck.level);
        state.set(stuck.level, park);
        steps.push(park);
    }
    return steps;
}
/** O indicador depois das gravacoes: o que mudou no lugar do que havia. */
function applied(current, changes) {
    const final = new Map(current.map((band) => [band.level, band]));
    for (const change of changes) {
        final.set(change.level, change);
    }
    return [...final.values()];
}
/**
 * Le a recusa de sobreposicao do servidor.
 *
 * As duas implementacoes respondem o mesmo texto - "As faixas EXCELLENT e GOOD se
 * sobrepõem entre 120000.00 e 125000: ..." -, com o nivel em ingles e o valor na
 * escala guardada. A tela ja evita o caso; isto e a rede para quando o servidor
 * enxerga algo que ela nao via (outra aba, outra pessoa salvando ao mesmo tempo).
 */
export function parseOverlap(message) {
    const match = /faixas\s+([A-Z_]+)\s+e\s+([A-Z_]+)\s+se\s+sobrep\S*\s+entre\s+(-?[\d.]+)\s+e\s+(-?[\d.]+)/i.exec(message);
    if (!match) {
        return undefined;
    }
    const from = Number(match[3]);
    const to = Number(match[4]);
    if (Number.isNaN(from) || Number.isNaN(to)) {
        return undefined;
    }
    return {
        level: match[1].toUpperCase(),
        other: match[2].toUpperCase(),
        from,
        to
    };
}
//# sourceMappingURL=save-order.js.map